import behance from 'src/assets/images/Behance.svg';
import codeChef from 'src/assets/images/codeChefBlue.svg';
import github from 'src/assets/images/github.svg';
import instagram from 'src/assets/images/instaBlue.svg';
import leetcode from 'src/assets/images/leetcode.svg';
import link from 'src/assets/images/link_blue.png';
import linkedin from 'src/assets/icons/LinkedinIconSolid.svg';
import x from 'src/assets/icons/twitter_bold.svg';
import youtube from 'src/assets/images/youtubeBlue.svg';
import deleteIcon from 'src/assets/icons/DeleteOrgIcon.svg';
import downloadIcon from 'src/assets/icons/download_icon.svg';
import renameIcon from 'src/assets/icons/rename_icon.svg';
// import duplicateIcon from 'src/assets/icons/duplicate_icon.svg';
// import shareIcon from 'src/assets/icons/share_icon.svg';

import {
  ResumeOptions,
  Link,
  ResumeDetails,
  Theme,
  ResumeTemplateCategoryType,
  FontFamily,
  FontSize,
  FontSizeLevel,
  LayoutSectionKey,
  SectionLayout,
  Spacing,
} from '../../pages/ResumeBuilder/types';

export const DEFAULT_EDUCATION_FIELDS = {
  grade: true,
  institution: true,
  location: true,
  dates: true,
};

/**
 * The least space a resume may put between words.
 *
 * A PDF records where each glyph sits, not where the words are: an extractor
 * decides a gap is a space by comparing it against a threshold. Some faces --
 * Lato most of all -- draw a space narrow enough that at display sizes it
 * falls under that threshold, and the candidate's name comes out of the file
 * as `JohnDoe`.
 *
 * `word-spacing` rather than `letter-spacing`, which is the same fix aimed one
 * level finer. Letter-spacing widens *every* gap, including the ones inside a
 * token that has no spaces -- and that put a space into the middle of people's
 * URLs, so `linkedin.com/in/name` extracted as `linkedin.com /in/name`. Across
 * 116 real resumes it broke 15 more links than develop did. Word-spacing moves
 * only the gaps that are already spaces: same fix for the name, zero links
 * broken, and none of the reflow that comes of widening every character.
 *
 * 0.05em measured on the Chromium the server prints with (129, not a dev
 * machine's 145 -- they disagree): 0.02em still merges under Lato, 0.05em
 * clears every face the builder offers. 0.08em was tried and recovers nothing
 * further, so this is the smaller intervention that does the whole job.
 */
export const MIN_WORD_SPACING = '0.05em';

export const DEFAULT_SPACING: Spacing = {
  // Matches the body line height the templates already rendered at, so every
  // template now starts from one known value instead of whichever per-variant
  // default (1.2 to 2.0) its text happened to use.
  lineHeight: 1.5,
  gap: 1,
  marginX: 12,
  marginY: 12,
};

// Templates render at a fixed 794px design width representing an A4 sheet's
// 210mm -- independent of on-screen zoom, which is a separate preview-level
// concern. Spacing settings are stored in mm; this is the one fixed ratio
// every template converts through so a slider value means the same physical
// distance everywhere.
export const RESUME_PAGE_WIDTH_MM = 210;
export const RESUME_PAGE_WIDTH_PX = 794;
export const mmToPx = (mm: number) =>
  (RESUME_PAGE_WIDTH_PX / RESUME_PAGE_WIDTH_MM) * mm;

/** Scales a template's own hardcoded px value by how far a spacing setting
 * has moved from its default -- at the default slider position this returns
 * `originalPx` unchanged, so introducing the setting doesn't shift any
 * template's current look; moving the slider scales it proportionally. */
export const scaleBySpacing = (
  originalPx: number,
  value: number,
  defaultValue: number,
) => (defaultValue ? originalPx * (value / defaultValue) : originalPx);

/** Multiplier for inter-element spacing. Deliberately not a bare ratio: the
 * gap slider bottoms out at 0, and a bare ratio would collapse every gap AND
 * every padding built on the same scale to nothing. Half-weighting keeps the
 * far ends usable -- 0.5x at minimum, 2x at maximum, exactly 1x at default. */
export const spacingGapScale = (value: number, defaultValue: number) =>
  defaultValue ? 0.5 + 0.5 * (value / defaultValue) : 1;

export const TWO_COLUMN_TEMPLATES: string[] = [
  'Timeless Standard',
  'Sleek Modern',
  'Minimalist Pro',
  'Zen Professional',
  'Persona',
  'Refined Persona',
  'Bold Executive',
  'Sleek Simplicity',
  'Creative Spark',
  'Innovative Flair',
  'Professional Edge',
  'Visual Clarity',
  'Clean Canvas',
];

export const DEFAULT_TWO_COLUMN_LAYOUT: SectionLayout = {
  left: [
    'experience',
    'projects',
    'education',
    'positionsOfResponsibility',
    'volunteerExperiences',
    'publications',
    'awardsAndAchievements',
  ],
  right: [
    'skills',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ],
  hiddenSections: [],
};

export const DEFAULT_SINGLE_COLUMN_LAYOUT: SectionLayout = {
  left: [
    'experience',
    'projects',
    'education',
    'positionsOfResponsibility',
    'volunteerExperiences',
    'publications',
    'skills',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
    'awardsAndAchievements',
  ],
  right: [],
  hiddenSections: [],
};

export const SECTION_LABELS: Record<LayoutSectionKey, string> = {
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  licensesAndCertification: 'Certifications',
  interestOrHobbies: 'Interests & Hobbies',
  languages: 'Languages',
  awardsAndAchievements: 'Awards & Achievements',
  publications: 'Publications',
  volunteerExperiences: 'Volunteer Experience',
  positionsOfResponsibility: 'Positions of Responsibility',
};

/**
 * Place any section key a layout doesn't mention yet.
 *
 * Every layout below lists its sections explicitly, and saved resumes persist
 * the layout they were created with. So a newly added section is missing from
 * all of them, and SectionColumn only renders keys the layout names -- the
 * section would be editable in the builder and silently absent from the resume.
 *
 * Missing keys join the column that already holds `experience`, which is the
 * main content column in both the one- and two-column templates (in the
 * sidebar-left templates, `left` is the sidebar and `right` is the content).
 */
export const withMissingSections = (layout: SectionLayout): SectionLayout => {
  const placed = new Set<LayoutSectionKey>([
    ...layout.left,
    ...layout.right,
    ...layout.hiddenSections,
  ]);
  const missing = (Object.keys(SECTION_LABELS) as LayoutSectionKey[]).filter(
    (key) => !placed.has(key),
  );
  if (!missing.length) return layout;

  return layout.right.includes('experience')
    ? { ...layout, right: [...layout.right, ...missing] }
    : { ...layout, left: [...layout.left, ...missing] };
};

const singleColumn = (order: LayoutSectionKey[]): SectionLayout =>
  withMissingSections({
    left: order,
    right: [],
    hiddenSections: [],
  });

/**
 * Per-template default section placement, capturing each template's original
 * hardcoded order so existing resumes (which have no saved layout) render
 * identically after the dynamic-layout migration. Templates not listed here
 * fall back to the generic two- or single-column default.
 */
const twoColumn = (
  left: LayoutSectionKey[],
  right: LayoutSectionKey[],
): SectionLayout => withMissingSections({ left, right, hiddenSections: [] });

export const TEMPLATE_DEFAULT_LAYOUTS: Record<string, SectionLayout> = {
  // Campus-placement order: a fresher's education and projects carry more
  // than a first internship does.
  Campus: singleColumn([
    'education',
    'projects',
    'experience',
    'positionsOfResponsibility',
    'skills',
    'licensesAndCertification',
    'awardsAndAchievements',
    'publications',
    'volunteerExperiences',
    'languages',
    'interestOrHobbies',
  ]),
  'Timeless Standard': DEFAULT_TWO_COLUMN_LAYOUT,
  'Sleek Modern': DEFAULT_TWO_COLUMN_LAYOUT,
  'Minimalist Pro': DEFAULT_TWO_COLUMN_LAYOUT,
  'Zen Professional': twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  Persona: twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  'Bold Executive': twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  'Refined Persona': twoColumn(
    [
      'skills',
      'awardsAndAchievements',
      'licensesAndCertification',
      'languages',
      'interestOrHobbies',
    ],
    ['experience', 'projects', 'education'],
  ),
  'Sleek Simplicity': twoColumn(
    [
      'skills',
      'licensesAndCertification',
      'languages',
      'interestOrHobbies',
      'awardsAndAchievements',
    ],
    ['experience', 'projects', 'education'],
  ),
  'Creative Spark': twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  'Innovative Flair': twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  'Professional Edge': twoColumn(
    [
      'skills',
      'licensesAndCertification',
      'languages',
      'interestOrHobbies',
      'awardsAndAchievements',
    ],
    ['experience', 'projects', 'education'],
  ),
  'Visual Clarity': twoColumn(
    ['skills', 'licensesAndCertification', 'languages', 'interestOrHobbies'],
    ['experience', 'projects', 'education', 'awardsAndAchievements'],
  ),
  'Clean Canvas': twoColumn(
    [
      'education',
      'skills',
      'licensesAndCertification',
      'languages',
      'interestOrHobbies',
    ],
    ['experience', 'projects', 'awardsAndAchievements'],
  ),
  'Pure Form': singleColumn([
    'experience',
    'projects',
    'education',
    'awardsAndAchievements',
    'licensesAndCertification',
    'skills',
    'languages',
    'interestOrHobbies',
  ]),
  'ATS Pro': singleColumn([
    'skills',
    'experience',
    'education',
    'projects',
    'awardsAndAchievements',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ]),
  'ATS Max': singleColumn([
    'skills',
    'experience',
    'education',
    'projects',
    'awardsAndAchievements',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ]),
  'ATS Optimus': singleColumn([
    'skills',
    'experience',
    'education',
    'projects',
    'awardsAndAchievements',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ]),
  'Metro Professional': singleColumn([
    'skills',
    'experience',
    'projects',
    'education',
    'awardsAndAchievements',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ]),
  'Career Blueprint': singleColumn([
    'skills',
    'experience',
    'projects',
    'education',
    'awardsAndAchievements',
    'licensesAndCertification',
    'languages',
    'interestOrHobbies',
  ]),
};

export const getDefaultSectionLayout = (
  templateName: string,
): SectionLayout => {
  if (TEMPLATE_DEFAULT_LAYOUTS[templateName]) {
    return TEMPLATE_DEFAULT_LAYOUTS[templateName];
  }
  if (TWO_COLUMN_TEMPLATES.includes(templateName)) {
    return DEFAULT_TWO_COLUMN_LAYOUT;
  }
  return DEFAULT_SINGLE_COLUMN_LAYOUT;
};

export const defaultResumeBuilderState: ResumeDetails = {
  title: 'Untitled',
  personalDetails: {
    firstName: '',
    lastName: '',
    avatar: '',
    email: '',
    phone: {
      countryCode: '91',
      number: '',
    },
    jobTitle: null,
    address: null,
    links: [
      {
        name: 'LinkedIn',
        url: null,
      },
    ],
  },
  experience: [],
  education: [],
  licensesAndCertification: [],
  skills: [],
  shortBio: '',
  projects: [],
  interestOrHobbies: [],
  languages: [],
  awardsAndAchievements: [],
};

export const resumeTabs: {
  label: string;
  value: string;
  icon: string;
  key: keyof ResumeDetails;
}[] = [
  {
    label: 'Personal Details',
    value: 'personal-details',
    icon: 'personal_details',
    key: 'personalDetails',
  },
  {
    label: 'Education',
    value: 'education-details',
    icon: 'education',
    key: 'education',
  },
  {
    label: 'Skills',
    value: 'skills-details',
    icon: 'skills',
    key: 'skills',
  },
  {
    label: 'Experience',
    value: 'experience-details',
    icon: 'experience',
    key: 'experience',
  },
  {
    label: 'Projects',
    value: 'projects-details',
    icon: 'projects',
    key: 'projects',
  },
  {
    label: 'Short Bio',
    value: 'short-bio-details',
    icon: 'bio',
    key: 'shortBio',
  },
  {
    label: 'Licence & Certification',
    value: 'license-and-certification-details',
    icon: 'certification',
    key: 'licensesAndCertification',
  },
  {
    label: 'Add More',
    value: 'add-more',
    icon: 'add_more',
    key: 'awardsAndAchievements',
  },
  {
    label: 'Customise',
    value: 'customise-section',
    icon: 'customise',
    key: 'title',
  },
];

export const suggestedLinks: (Link & { icon: string })[] = [
  { name: 'Personal Website', url: null, icon: link },
  { name: 'LinkedIn', url: null, icon: linkedin },
  { name: 'X / Twitter', url: null, icon: x },
  { name: 'Leetcode', url: null, icon: leetcode },
  { name: 'CodeChef', url: null, icon: codeChef },
  { name: 'Medium', url: null, icon: link },
  { name: 'IMDb', url: null, icon: link },
  { name: 'GitHub', url: null, icon: github },
  { name: 'Figma', url: null, icon: link },
  { name: 'Fiverr', url: null, icon: link },
  { name: 'Instagram', url: null, icon: instagram },
  { name: 'Dribbble', url: null, icon: link },
  { name: 'Behance', url: null, icon: behance },
  { name: 'Stack Overflow', url: null, icon: link },
  { name: 'Quora', url: null, icon: link },
  { name: 'Academia', url: null, icon: link },
  { name: 'Facebook', url: null, icon: link },
  { name: 'YouTube', url: null, icon: youtube },
  { name: 'Tumblr', url: null, icon: link },
  { name: 'Pinterest', url: null, icon: link },
  { name: 'WordPress', url: null, icon: link },
  { name: 'Other', url: null, icon: link },
];

export const resumeProgressMapping: Record<keyof ResumeDetails, number> = {
  title: 0,
  template: 0,
  personalDetails: 20,
  experience: 15,
  education: 20,
  licensesAndCertification: 10,
  skills: 10,
  shortBio: 10,
  projects: 10,
  interestOrHobbies: 1,
  languages: 1,
  awardsAndAchievements: 1,
  publications: 1,
  volunteerExperiences: 1,
  positionsOfResponsibility: 1,
  enhancedShortBio: 0,
  currentVersion: 0,
  enhancedSkills: 0,
};

export const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const languageProficiencyOptions = [
  'Beginner',
  'Conversational',
  'Intermediate',
  'Fluent',
  'Native / Professional',
];

export const myResumeOptions: {
  title: ResumeOptions;
  icon: string;
}[] = [
  {
    title: 'Download',
    icon: downloadIcon,
  },
  {
    title: 'Rename',
    icon: renameIcon,
  },
  // {
  //   title: 'Duplicate',
  //   icon: duplicateIcon,
  // },
  // {
  //   title: 'Share',
  //   icon: shareIcon,
  // },
  {
    title: 'Delete',
    icon: deleteIcon,
  },
];

export const ResumeThemes: Theme[] = [
  {
    name: 'Default',
    value: '#0E6CC2',
  },
  {
    name: 'Yellow',
    value: '#F9A822',
  },
  {
    name: 'Green',
    value: '#388E3C',
  },
  {
    name: 'Red',
    value: '#DF1527',
  },
  {
    name: 'Orange',
    value: '#FF3D00',
  },
  {
    name: 'Purple',
    value: '#25517C',
  },
  {
    name: 'Cannon Pink',
    value: '#8E4585',
  },
  {
    name: 'Woodland',
    value: '#374521',
  },
  {
    name: 'Zorba',
    value: '#A89B89', // 8
  },
  {
    name: 'Dark Grey',
    value: '#555',
  },
  {
    name: 'Golden Grass',
    value: '#D6A021',
  },
  {
    name: 'Tuscany',
    value: '#C76B2E',
  },
  {
    name: 'Old Brick',
    value: '#8B1E1A',
  },
  {
    name: 'Chambray',
    value: '#2E5984',
  },
  {
    name: 'Tahiti Gold', // 14
    value: '#D97706',
  },
  {
    name: 'Surfie Green',
    value: '#0F766E',
  },
  {
    name: 'Well Read',
    value: '#B03A2E',
  },
  {
    name: 'Costa Del Sol',
    value: '#556B2F',
  },
  {
    name: 'Deep Blush',
    value: '#E77092',
  },
  {
    name: 'Sandy brown',
    value: '#F4A261', // 19
  },
  {
    name: 'Steel Blue',
    value: '#3D7EA6',
  },
  {
    name: 'Sepia Skin',
    value: '#9C5E3F',
  },
  {
    name: 'Eucalyptus',
    value: '#227F58',
  },
  {
    name: 'Royal Blue',
    value: '#3575E5',
  },
  {
    name: 'Sunglow',
    value: '#FFC437',
  },
  {
    name: 'Tangerine',
    value: '#F28500',
  },
  {
    name: 'Cranberry',
    value: '#D95980',
  },
  {
    name: 'Apple Blossom',
    value: '#A23E48',
  },
  {
    name: 'Trinidad',
    value: '#F25C05',
  },
  {
    name: 'Meteor',
    value: '#C47F17', // 29
  },
  {
    name: 'Blumine',
    value: '#1E4A7B',
  },
  {
    name: 'Plum',
    value: '#8E3B8E',
  },
  {
    name: 'Copper',
    value: '#A66D2F',
  },
  {
    name: 'Tall Poppy',
    value: '#B43424',
  },
  {
    name: 'Butterfly Bush',
    value: '#5E548E',
  },
  {
    name: 'Oracle',
    value: '#357266',
  },
  {
    name: 'Cello',
    value: '#1E3A5F',
  },
  {
    name: 'Mexican Red',
    value: '#A62349',
  },
  {
    name: 'Ecstasy',
    value: '#F57F17',
  },
  {
    name: 'Tropical Rain Forest',
    value: '#006D5B',
  },
  {
    name: 'Affair',
    value: '#733D90',
  },
  {
    name: 'Hot Toddy',
    value: '#B8860B',
  },
  {
    name: 'Fruit Salad',
    value: '#5D9C59',
  },
  {
    name: 'Amaranth',
    value: '#E63946',
  },
  {
    name: 'Nepal',
    value: '#81A8B7', // 44
  },
  {
    name: 'Battleship Gray',
    value: '#7E8F74',
  },
  {
    name: 'Turkish Rose',
    value: '#B77B82',
  },
  {
    name: 'Trendy Pink',
    value: '#8C6A9F',
  },
  {
    name: 'Antique Brass',
    value: '#C48E6A',
  },
  {
    name: 'Curious Blue', // 49
    value: '#1E88E5',
  },
  {
    name: 'Cardinal',
    value: '#BC2023',
  },
  {
    name: 'Elm',
    value: '#227B87',
  },
  // The four accents the single-column ATS designs were drawn in. The palette
  // had no muted sage or plum, and its nearest greens/greys read quite
  // differently at the 8% tint Sage Field lays behind its body.
  {
    name: 'Sage', // 52
    value: '#446049',
  },
  {
    name: 'Steel',
    value: '#3B5972',
  },
  {
    name: 'Rust',
    value: '#784F41',
  },
  {
    name: 'Plum',
    value: '#604966',
  },
  {
    name: 'Charcoal', // 56
    value: '#191B1E',
  },
  {
    name: 'Oxford', // 57
    value: '#1F3A60',
  },
];

export const ResumeTemplateCategory: ResumeTemplateCategoryType[] = [
  'ATS',
  'Modern',
  'Creative',
  'Professional',
];

export const resumeBuilderRichTextEditorModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    ['link', 'formula'],
    ['clean'],
  ],
};

export const ADD_MORE_SECTION_LIST: {
  title: string;
  value: keyof ResumeDetails;
  icon: string;
}[] = [
  {
    title: 'Awards And Achievements',
    value: 'awardsAndAchievements',
    icon: 'awards',
  },
  {
    title: 'Languages',
    value: 'languages',
    icon: 'languages',
  },
  {
    title: 'Hobbies & Interest',
    value: 'interestOrHobbies',
    icon: 'hobbies',
  },
  {
    title: 'Publications',
    value: 'publications',
    // Reusing the certification glyph: the sprite has no publication icon yet,
    // and a document mark is the closest thing in it.
    icon: 'certification',
  },
  {
    title: 'Volunteer Experience',
    value: 'volunteerExperiences',
    icon: 'experience',
  },
  {
    title: 'Positions of Responsibility',
    value: 'positionsOfResponsibility',
    icon: 'experience',
  },
];

export const FontList: FontFamily[] = [
  'Inter',
  'Poppins',
  'Tinos',
  'Open Sans',
  'Lato',
  'Carlito',
];

export const FontSizeList: Record<FontSizeLevel, FontSize> = {
  xs: { level: 'xs', value: -4 },
  s: { level: 's', value: -2 },
  m: { level: 'm', value: 0 },
  l: { level: 'l', value: 2 },
  xl: { level: 'xl', value: 4 },
};

export const PROMPT_LIST = [
  {
    type: 'diagnosis',
    prompt: 'Make my resume ATS-friendly and highlight leadership.',
  },
  { type: 'business', prompt: 'Enhance my resume for IT managerial roles.' },
  {
    type: 'neurology',
    prompt: 'Polish my skills for top-tier job interviews.',
  },
  {
    type: 'diagnosis',
    prompt: 'Find gaps in my resume and suggest improvements.',
  },
  {
    type: 'neurology',
    prompt: 'Optimize my resume using cognitive principles.',
  },
  { type: 'diagnosis', prompt: 'Highlight weak sections and propose fixes.' },
  {
    type: 'business',
    prompt: 'Emphasize entrepreneurial experience and acumen.',
  },
  {
    type: 'neurology',
    prompt: 'Use persuasive language to enhance my resume.',
  },
  {
    type: 'diagnosis',
    prompt: 'Match my resume to job-specific requirements.',
  },
  { type: 'business', prompt: 'Tailor my resume for executive global roles.' },
  { type: 'neurology', prompt: 'Enhance readability with visual hierarchy.' },
  {
    type: 'business',
    prompt: 'Reframe skills for leadership and cross-functional roles.',
  },
];

export const RESUME_PARSING_ISSUES = [
  'The uploaded document is of low quality, making it hard to read.',
  'Your resume might have been exported from Figma, causing text to jumble.',
  'The file appears to be corrupted or in an unsupported format.',
  'The resume contains unrecognized characters or overly complex layouts.',
  'The document is password-protected or encrypted.',
  'Your resume includes embedded images instead of plain text.',
];

export const UPLOADING_MESSAGES = [
  'Buckle up! Your resume is soaring across the globe to get the best treatment.',
  'Fasten your seatbelt—upload in progress!',
  "We're extracting your resume details with precision—this won't take long.",
  'Hang tight! Your resume is being prepped for a smoother journey.',
  'Almost there—just a few more seconds to extract the important bits!',
  'Done soon! Your resume is ready to take the next step with us.',
];

export const ENHANCING_PLACEHOLDERS = [
  'Optimizing your resume to be ATS-friendly for better recruiter visibility.',
  'Highlighting your skills and achievements to make you stand out.',
  'Crafting professional, tailored content for top industry standards.',
  "Enhancing layout and readability to catch recruiters' attention quickly.",
  'Boosting your chances of landing interviews with a polished resume.',
];

export const RESUME_TEMPLATES = [
  // Single-column ATS set. No background: each is a plain linear flow, so the
  // page's own margins are their only top and bottom spacing.
  {
    name: 'Signature',
    availableThemes: [
      // Oxford is the default look. The row's default_theme has to say so:
      // the no-default fallback picks by palette order, not by this list's.
      ResumeThemes[57].value,
      ResumeThemes[53].value,
      ResumeThemes[52].value,
      ResumeThemes[54].value,
      ResumeThemes[55].value,
      ResumeThemes[50].value,
      ResumeThemes[56].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Campus',
    availableThemes: [
      // Charcoal (monochrome) is the default look; set it as the row's
      // default_theme for the same reason as Signature's.
      ResumeThemes[56].value,
      ResumeThemes[57].value,
      ResumeThemes[53].value,
      ResumeThemes[52].value,
      ResumeThemes[54].value,
      ResumeThemes[55].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Classic Center',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Ledger Gutter',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Steel Rule',
    availableThemes: [
      // Steel Rule's own accent first: it is this template's default look.
      ResumeThemes[53].value,
      ResumeThemes[52].value,
      ResumeThemes[55].value,
      ResumeThemes[0].value,
      ResumeThemes[13].value,
      ResumeThemes[6].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Dense Brief',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Sage Field',
    availableThemes: [
      // Sage Field's own accent first: it is this template's default look.
      ResumeThemes[52].value,
      ResumeThemes[53].value,
      ResumeThemes[54].value,
      ResumeThemes[55].value,
      ResumeThemes[7].value,
      ResumeThemes[15].value,
    ],
    isSinglePage: false,
    // Paints its own header band / body panel, so it reaches the sheet's edges
    // rather than being inset by the page margin.
    hasBackground: true,
  },
  {
    name: 'Dark Band',
    availableThemes: [
      // Dark Band's own accent first: it is this template's default look.
      ResumeThemes[56].value,
      ResumeThemes[53].value,
      ResumeThemes[52].value,
      ResumeThemes[54].value,
      ResumeThemes[55].value,
      ResumeThemes[7].value,
    ],
    isSinglePage: false,
    // Paints its own header band / body panel, so it reaches the sheet's edges
    // rather than being inset by the page margin.
    hasBackground: true,
  },
  {
    name: 'Portrait Serif',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Letterhead',
    availableThemes: [
      // Letterhead's own accent first: it is this template's default look.
      ResumeThemes[54].value,
      ResumeThemes[53].value,
      ResumeThemes[52].value,
      ResumeThemes[55].value,
      ResumeThemes[11].value,
      ResumeThemes[12].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Editorial Serif',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Wide Open',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Timeless Standard',
    availableThemes: [
      ResumeThemes[0].value,
      ResumeThemes[2].value,
      ResumeThemes[3].value,
      ResumeThemes[4].value,
      ResumeThemes[5].value,
      ResumeThemes[7].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Pure Form',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'ATS Pro',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'ATS Max',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'ATS Optimus',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Sleek Modern',
    availableThemes: [
      ResumeThemes[0].value,
      ResumeThemes[7].value,
      ResumeThemes[8].value,
      ResumeThemes[9].value,
      ResumeThemes[10].value,
      ResumeThemes[11].value,
      ResumeThemes[12].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Metro Professional',
    availableThemes: [
      ResumeThemes[6].value,
      ResumeThemes[36].value,
      ResumeThemes[37].value,
      ResumeThemes[38].value,
      ResumeThemes[39].value,
      ResumeThemes[41].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Minimalist Pro',
    availableThemes: [],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Professional Edge',
    availableThemes: [
      ResumeThemes[36].value,
      ResumeThemes[37].value,
      ResumeThemes[38].value,
      ResumeThemes[39].value,
      ResumeThemes[40].value,
      ResumeThemes[41].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Sleek Simplicity',
    availableThemes: [
      ResumeThemes[30].value,
      ResumeThemes[31].value,
      ResumeThemes[32].value,
      ResumeThemes[33].value,
      ResumeThemes[34].value,
      ResumeThemes[35].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Zen Professional',
    availableThemes: [
      ResumeThemes[42].value,
      ResumeThemes[19].value,
      ResumeThemes[43].value,
      ResumeThemes[31].value,
      ResumeThemes[20].value,
      ResumeThemes[29].value,
    ],
    isSinglePage: false,
    hasBackground: false,
  },
  {
    name: 'Creative Spark',
    availableThemes: [
      ResumeThemes[6].value,
      ResumeThemes[13].value,
      ResumeThemes[14].value,
      ResumeThemes[15].value,
      ResumeThemes[16].value,
      ResumeThemes[17].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Persona',
    availableThemes: [
      ResumeThemes[18].value,
      ResumeThemes[19].value,
      ResumeThemes[20].value,
      ResumeThemes[21].value,
      ResumeThemes[22].value,
      ResumeThemes[23].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Innovative Flair',
    availableThemes: [
      ResumeThemes[24].value,
      ResumeThemes[25].value,
      ResumeThemes[26].value,
      ResumeThemes[27].value,
      ResumeThemes[28].value,
      ResumeThemes[29].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Visual Clarity',
    availableThemes: [
      ResumeThemes[44].value,
      ResumeThemes[8].value,
      ResumeThemes[45].value,
      ResumeThemes[46].value,
      ResumeThemes[47].value,
      ResumeThemes[48].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Bold Executive',
    availableThemes: [
      ResumeThemes[43].value,
      ResumeThemes[49].value,
      ResumeThemes[50].value,
      ResumeThemes[14].value,
      ResumeThemes[42].value,
      ResumeThemes[31].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Clean Canvas',
    availableThemes: [
      ResumeThemes[14].value,
      ResumeThemes[35].value,
      ResumeThemes[42].value,
      ResumeThemes[34].value,
      ResumeThemes[31].value,
      ResumeThemes[49].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Career Blueprint',
    availableThemes: [
      ResumeThemes[51].value,
      ResumeThemes[14].value,
      ResumeThemes[31].value,
      ResumeThemes[32].value,
      ResumeThemes[42].value,
      ResumeThemes[43].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
  {
    name: 'Refined Persona',
    availableThemes: [
      ResumeThemes[34].value,
      ResumeThemes[14].value,
      ResumeThemes[51].value,
      ResumeThemes[32].value,
      ResumeThemes[42].value,
      ResumeThemes[43].value,
    ],
    isSinglePage: false,
    hasBackground: true,
  },
] as const;
